import { useQuery } from '@tanstack/react-query';
import { useHybridAuth } from '../../hooks/useHybridAuth';
import { QueryConfig } from '../client';
import { VehicleMakesResponse, VehicleMake } from '../types/VehicleModels';
import env from '../../config/env';

interface RequestParams {
  companyId?: string;
}

const getVehicleMakesRequest = async (
  token: string,
  options?: RequestParams
): Promise<VehicleMake[]> => {
  try {
    console.log('Fetching vehicle makes from API');
    const response = await fetch(`${env.apiUrl}/vehicle-models/makes`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json() as VehicleMakesResponse;
    // Ensure we always return an array, even if the response is malformed
    return Array.isArray(data.makes) ? data.makes : [];
  } catch (error) {
    console.error('Error fetching vehicle makes:', error);
    // Return an empty array on error instead of throwing
    return [];
  }
};

export const useGetVehicleMakes = (
  params?: RequestParams,
  config?: QueryConfig<typeof getVehicleMakesRequest>
) => {
  const { getAccessTokenSilently } = useHybridAuth();
  
  return useQuery({
    ...config,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getVehicleMakesRequest(token, params);
    },
    queryKey: ['vehicle-makes', params],
    staleTime: 1000 * 60 * 60,      // 1 hour - data stays fresh for 1 hour
    gcTime: 1000 * 60 * 60,         // 1 hour - cache retained for 1 hour after going stale
    retry: 1,                        // Reduce retry attempts from default 3 to 1
    retryDelay: (attemptIndex) => Math.min(1000 * (2 ** attemptIndex), 5000), // Cap at 5 seconds
  });
};
